import React, { memo, NamedExoticComponent, useState, MouseEvent } from 'react'
import { useDispatch } from 'react-redux'
import DeleteIcon from 'transfix-icons/build/DeleteIcon'
import InfoIcon from 'transfix-icons/build/InfoIcon'
import { Button } from 'transfix-ui/components/Button'
import { IconButton } from 'transfix-ui/components/IconButton'
import { FormModal } from 'transfix-ui/components/Modals'
import { Switch } from 'transfix-ui/components/Switch'
import { Table, TableBody, TableCell, TableRow } from 'transfix-ui/components/Table'
import { TextArea } from 'transfix-ui/components/TextArea'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { IDigestEmail } from 'types'
import { ICarrierTaskAutomationConfig } from 'types/carrier'
import {
  editContactInstructions,
  closeContactInstructionsModal,
  editCarrierTaskAutomationConfig,
} from 'utils/actions/carrier'
import { IUserOption } from 'utils/selectors/users'
import { DispatcherSelect } from 'apps/marketplace/components/DispatcherSelect'

interface IInstructionsDialog {
  instructionText: string
  bulkEmailEnabled: boolean
  carrierBulkEmailRecipients: IDigestEmail[]
  error?: string
  saving: boolean
  id: string
  taskAutomationConfig?: ICarrierTaskAutomationConfig
}
/** @deprecated */
export const InstructionsDialogLegacy: NamedExoticComponent<IInstructionsDialog> = memo(
  ({
    bulkEmailEnabled,
    carrierBulkEmailRecipients,
    error,
    id: carrierId,
    instructionText,
    saving,
    taskAutomationConfig,
  }) => {
    const dispatch = useDispatch()
    const closeDialogHandler = () => dispatch(closeContactInstructionsModal())

    const [localText, setLocalText] = useState(instructionText)
    const updateText = (e: React.ChangeEvent<HTMLTextAreaElement>) =>
      setLocalText(e.currentTarget.value)
    const [dailyLoadDigest, setDailyLoadDigest] = useState<boolean>(bulkEmailEnabled)
    const toggleDigest = () => setDailyLoadDigest(!dailyLoadDigest)
    const [digestUser, setDigestUser] = useState<IDigestEmail>()
    const [disableButton, setDisableButton] = useState<boolean>(false)
    const [recipients, setUpdateRecipients] = useState<Partial<IDigestEmail>[]>(
      carrierBulkEmailRecipients || []
    )
    const [dispatcherError, setDispatcherError] = useState<boolean>(false)

    const [canAutomateCheckEta, setCanAutomateCheckEta] = useState<boolean>(
      taskAutomationConfig?.canAutomateCheckEta ?? false
    )
    const toggleCanAutomateCheckEta = () => {
      setCanAutomateCheckEta(!canAutomateCheckEta)
    }

    const [canAutomateCheckin, setCanAutomateCheckin] = useState<boolean>(
      taskAutomationConfig?.canAutomateCheckin ?? false
    )
    const toggleCanAutomateCheckin = () => {
      setCanAutomateCheckin(!canAutomateCheckin)
    }

    const [canAutomateCheckout, setCanAutomateCheckout] = useState<boolean>(
      taskAutomationConfig?.canAutomateCheckout ?? false
    )
    const toggleCanAutomateCheckout = () => {
      setCanAutomateCheckout(!canAutomateCheckout)
    }

    const [canAutomateDispatch, setCanAutomateDispatch] = useState<boolean>(
      taskAutomationConfig?.canAutomateDispatch ?? false
    )
    const toggleCanAutomateDispatch = () => {
      setCanAutomateDispatch(!canAutomateDispatch)
    }

    const deleteRecipient = (e: MouseEvent<HTMLButtonElement>) => {
      const userId = e.currentTarget.value
      const newRecipients = recipients
        .map(recipient => {
          if (String(recipient.userId) === userId) {
            // If recipient already exists in db, turn on destroy
            if (recipient.id) recipient._destroy = true
            // else if recipient only exist on FE, return an empty object to filter out
            else return {}
          }
          return recipient
        })
        // filter out any recipient that doesn't have a userId
        .filter(recipient => recipient.userId)
      setUpdateRecipients(newRecipients)
    }

    // Update recipients with updated assignment
    const addDigestRecipient = () => {
      if (digestUser) {
        const isUserAlreadyRecipient = recipients.some(
          recipient => String(recipient.userId) === digestUser.userId
        )
        const newRecipients = isUserAlreadyRecipient
          ? recipients.map(recipient => {
              if (String(recipient.userId) === digestUser.userId) {
                recipient._destroy = false
              }
              return recipient
            })
          : [...recipients, digestUser]
        setUpdateRecipients(newRecipients)
        setDigestUser(undefined)
      } else {
        setDispatcherError(true)
      }
    }

    const onDispatcherChange = (dispatcher: Partial<IUserOption>) => {
      if (dispatcher) {
        setDispatcherError(false)
        const isUserAlreadyRecipient = recipients.some(
          recipient => String(recipient.userId) === dispatcher.value && recipient._destroy !== true
        )
        const currentRecipient = isUserAlreadyRecipient
          ? recipients.find(recipient => String(recipient.userId) === dispatcher.value)
          : ''
        setDisableButton(isUserAlreadyRecipient)
        setDigestUser({
          id: currentRecipient ? currentRecipient.id : undefined,
          carrierApplicationId: carrierId,
          userId: dispatcher.value || '',
          name: dispatcher.label || '',
          _destroy: false,
        })
      }
    }

    const saveForm = () => {
      const contactPayload = {
        carrierSpecificInfo: localText,
        bulkEmailEnabled: dailyLoadDigest,
        carrierBulkEmailRecipientsAttributes: recipients,
      }
      dispatch(editContactInstructions(carrierId, contactPayload))

      const taskAutomationConfigPayload = {
        canAutomateCheckEta,
        canAutomateCheckin,
        canAutomateCheckout,
        canAutomateDispatch,
      }
      dispatch(editCarrierTaskAutomationConfig(carrierId, taskAutomationConfigPayload))
    }

    return (
      <FormModal
        title='Contact Instructions'
        size={6}
        open
        onClose={closeDialogHandler}
        error={error}
        dataTest='modal-contact-instructions'
        actions={[
          {
            label: 'Cancel',
            type: 'dismiss',
            disabled: saving,
          },
          {
            label: 'Save',
            type: 'confirm',
            action: saveForm,
            disabled: saving,
            dataTest: 'update-carrier-instructions',
          },
        ]}>
        <div className='flex items-end mb4 mt2'>
          <Switch
            checked={dailyLoadDigest}
            dataTest='send-daily-digest-toggle'
            label='Send Daily Load Digest'
            onChange={toggleDigest}
          />
          <Tooltip
            trigger={
              <span className='ml2'>
                <InfoIcon color='coolGray5' size='large' />
              </span>
            }>
            <div>
              <p>
                When this option is turned on,
                <br /> carrier will receive a daily email
                <br /> requesting any updates
                <br /> needed for active or recently
                <br /> completed loads.
              </p>
            </div>
          </Tooltip>
        </div>
        {dailyLoadDigest && (
          <>
            <div>
              <div className='mt5'>
                <strong>Daily load digest recipients</strong>
              </div>
              <div className='flex items-start mt3'>
                <div className='mt1'>
                  <DispatcherSelect
                    carrierId={carrierId}
                    dispatcherId={digestUser?.userId}
                    onChange={onDispatcherChange}
                  />
                </div>
                <Button
                  className='ml3 mt5'
                  color='secondary'
                  onClick={addDigestRecipient}
                  disabled={disableButton}
                  data-test='add-digest-recipient'>
                  Add Digest Recipient
                </Button>
              </div>
            </div>
            {dispatcherError && <p className='text-error'>Please select a dispatcher</p>}
            <div className='mt5'>
              <Table>
                <></>
                <TableBody>
                  {recipients
                    .filter(recipient => !recipient._destroy)
                    .map(recipient => (
                      <TableRow key={recipient?.userId}>
                        <TableCell className='pl0'>
                          <div className='flex'>
                            <span className='w-100'>
                              {recipient?.userFullName || recipient?.name}
                            </span>
                            <div className='flex-row-reverse'>
                              <IconButton
                                Icon={DeleteIcon}
                                color='tertiary'
                                value={recipient.userId}
                                onClick={deleteRecipient}
                                data-test={`delete-recipient-${recipient.userId}`}
                                label='Delete'
                                size='large'
                              />
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
        <hr />
        <div className='flex items-end mb4 mt2'>
          <Switch
            checked={canAutomateCheckEta}
            dataTest='can-automate-check-eta-toggle'
            label='Automate Check ETA'
            onChange={toggleCanAutomateCheckEta}
          />
        </div>
        <div className='flex items-end mb4 mt2'>
          <Switch
            checked={canAutomateCheckin}
            dataTest='can-automate-checkin-toggle'
            label='Automate Check In'
            onChange={toggleCanAutomateCheckin}
          />
        </div>
        <div className='flex items-end mb4 mt2'>
          <Switch
            checked={canAutomateCheckout}
            dataTest='can-automate-checkout-toggle'
            label='Automate Check Out'
            onChange={toggleCanAutomateCheckout}
          />
        </div>
        <div className='flex items-end mb4 mt2'>
          <Switch
            checked={canAutomateDispatch}
            dataTest='can-automate-dispatch-toggle'
            label='Automate Dispatch'
            onChange={toggleCanAutomateDispatch}
          />
        </div>

        <hr />
        <div className='mt5'>
          <TextArea
            size='large'
            label='Notes'
            placeholder='Add Contact Instructions...'
            name='instructions'
            data-test='contact-instructions'
            value={localText}
            onChange={updateText}
          />
        </div>
      </FormModal>
    )
  }
)

export default InstructionsDialogLegacy
